import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Award, Trophy, ArrowRight, Star, BookOpen } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClubLeaderboard() {
  const [leaders, setLeaders] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/club/leaderboard`).then(r => r.json()).then(setLeaders).catch(() => {});
  }, []);

  return (
    <Layout>
      <div className="min-h-screen bg-[#0a0a0f]">
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-3xl">
            <div className="text-center mb-12">
              <p className="uppercase tracking-[0.2em] text-xs font-bold text-amber-400 mb-4">Leaderboard</p>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3" data-testid="leaderboard-title">
                Top <span className="text-amber-400">Reviewers</span>
              </h1>
              <p className="text-gray-400 text-sm max-w-lg mx-auto">
                Our most active members helping fellow authors succeed. Review books to climb the ranks and earn badges.
              </p>
            </div>

            {/* Badge Legend */}
            <div className="flex flex-wrap justify-center gap-4 mb-10">
              {[
                { name: 'Bronze', color: '#CD7F32', min: 5 },
                { name: 'Silver', color: '#C0C0C0', min: 15 },
                { name: 'Gold', color: '#D4AF37', min: 30 },
                { name: 'Platinum', color: '#E5E4E2', min: 50 },
              ].map(b => (
                <span key={b.name} className="flex items-center gap-1.5 text-xs" style={{ color: b.color }}>
                  <Award className="w-3 h-3" /> {b.name} ({b.min}+ reviews)
                </span>
              ))}
            </div>

            {/* Leaderboard Table */}
            {leaders.length === 0 ? (
              <div className="text-center py-16 bg-gray-900/50 border border-gray-800 rounded-lg" data-testid="leaderboard-empty">
                <Trophy className="w-10 h-10 text-amber-400/30 mx-auto mb-4" />
                <p className="text-gray-400 mb-2">No reviewers yet. Be the first!</p>
                <Link to="/club/dashboard" className="text-sm text-amber-400 hover:underline">Join The Exchange</Link>
              </div>
            ) : (
              <div className="space-y-2" data-testid="leaderboard-list">
                {leaders.map((l, i) => (
                  <Card key={i} className={`border ${i === 0 ? 'bg-amber-500/10 border-amber-500/30' : i < 3 ? 'bg-gray-900/50 border-gray-800' : 'bg-gray-900/30 border-gray-800/50'}`}
                    data-testid={`leader-${i}`}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-8 text-center">
                        {i === 0 ? <Trophy className="w-5 h-5 text-amber-400 mx-auto" /> : (
                          <span className="text-lg font-bold text-gray-500">{l.rank}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-base font-semibold text-white truncate">{l.name}</p>
                          {l.badge && (
                            <span className="flex items-center gap-1 text-[10px] font-bold" style={{ color: l.badge.color }}>
                              <Award className="w-3 h-3" /> {l.badge.name}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm shrink-0">
                        <span className="flex items-center gap-1 text-gray-400"><Star className="w-3 h-3" /> {l.total_reviews}</span>
                        <span className="flex items-center gap-1 text-gray-400"><BookOpen className="w-3 h-3" /> {l.total_books}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* CTA */}
            <div className="text-center mt-12">
              <Link to="/club/dashboard">
                <Button data-testid="lb-join-cta" className="bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full px-8 py-6">
                  Join The Exchange <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
